import { Box } from "@mui/material";

export const badgeTones = {
  success: {
    background: ["green", 50],
    border: ["green", 200],
    text: ["green", 700],
  },
  warning: {
    background: ["orange", 50],
    border: ["orange", 200],
    text: ["orange", 700],
  },
  info: {
    background: ["purple", 50],
    border: ["purple", 200],
    text: ["purple", 700],
  },
  blue: {
    background: ["blue", 50],
    border: ["blue", 200],
    text: ["blue", 700],
  },
  error: {
    background: ["red", 50],
    border: ["red", 200],
    text: ["red", 700],
  },
  neutral: {
    background: ["gray", 50],
    border: ["gray", 200],
    text: ["gray", 600],
  },
  primary: {
    background: ["purple", 100],
    border: ["purple", 300],
    text: ["purple", 800],
  },
};

const sizeStyles = {
  sm: {
    fontSize: 12,
    minHeight: 24,
    px: 1,
  },
  md: {
    fontSize: 12,
    minHeight: 28,
    px: 1.25,
  },
  lg: {
    fontSize: 13,
    minHeight: 32,
    px: 1.5,
  },
};

const radiusByShape = {
  rounded: "sm",
  pill: "pill",
};

const getPaletteValue = (theme, [scale, shade]) => {
  if (scale === "common") return theme.palette.common?.[shade];

  return theme.palette.custom?.[scale]?.[shade];
};

export const getBadgeToneStyles = (theme, tone) => {
  const toneTokens = badgeTones[tone] || badgeTones.neutral;

  return {
    backgroundColor: getPaletteValue(theme, toneTokens.background),
    borderColor: getPaletteValue(theme, toneTokens.border),
    color: getPaletteValue(theme, toneTokens.text),
  };
};

export default function BadgeBase({
  children,
  tone = "neutral",
  size = "md",
  shape = "rounded",
  icon,
  endIcon,
  minWidth,
  sx,
  ...props
}) {
  const sizing = sizeStyles[size] || sizeStyles.md;
  const badgeSx = (theme) => {
    const toneStyle = getBadgeToneStyles(theme, tone);
    const radiusKey = radiusByShape[shape] || radiusByShape.rounded;

    return {
      alignItems: "center",
      backgroundColor: toneStyle.backgroundColor,
      border: "1px solid",
      borderColor: toneStyle.borderColor,
      borderRadius: theme.shape.radii[radiusKey],
      color: toneStyle.color,
      display: "inline-flex",
      fontFamily: theme.typography.fontFamily,
      flexShrink: 0,
      fontWeight: 700,
      gap: 0.75,
      justifyContent: "center",
      lineHeight: 1,
      maxWidth: "none",
      minWidth,
      textAlign: "center",
      whiteSpace: "nowrap",
      ...sizing,
    };
  };

  return (
    <Box
      component="span"
      sx={Array.isArray(sx) ? [badgeSx, ...sx] : [badgeSx, sx]}
      {...props}
    >
      {icon ? (
        <Box component="span" sx={{ display: "inline-flex", flexShrink: 0 }}>
          {icon}
        </Box>
      ) : null}
      <Box
        component="span"
        sx={{
          minWidth: 0,
          overflow: "visible",
          textOverflow: "clip",
          whiteSpace: "nowrap",
        }}
      >
        {children}
      </Box>
      {endIcon ? (
        <Box component="span" sx={{ display: "inline-flex", flexShrink: 0 }}>
          {endIcon}
        </Box>
      ) : null}
    </Box>
  );
}
